import React, {useEffect, useMemo} from 'react';
import {RouteInfo} from 'sfccAPI/interfaces';

import {EEventSource} from 'coreClasses/events/IEvents';
import withSSRPage from 'coreComponents/withSSRPage';
import {useClientServerEffect, useLocalesMgr, useLocation, usePageInit, useSetUI} from 'coreHooks';
import {EHeaderType} from 'coreStore/data/ui/types';
import buildPageMetadata from 'coreUtils/buildPageMetadata';
import {absoluteUrl} from 'coreUtils/url';

import {useGeneralProvider} from 'brand/app/components/_app-config/GeneralProvider';
import {MAX_CACHE_AGE} from 'brand/app/constants';
import {Contentful} from 'brand/app/contentful/components';
import {useHandlePageEvents} from 'brandHooks/useHandlePageEvents';

// eslint-disable-next-line no-restricted-imports
import './index.scss';

interface Props {
	content: object
	localizedContent: object
	isHomePage: boolean
	routeInfo: RouteInfo
	slug: string
}

const CLPEffects = (
	{
		isHomePage,
		content,
		slug
	}: Props) => {
	const localesMgr = useLocalesMgr();
	const currentLocale = localesMgr.getCurrentLocale();
	const {managerRepository} = useGeneralProvider();
	const cmsProvider = managerRepository.getContentMgr().getCMSProvider();

	useEffect(() => {
		localesMgr.updateUrl(content?.slug?.[cmsProvider.getCurrentLanguage()] || content?.slug?.[currentLocale.getLanguageCode()] || slug);
	}, [currentLocale.getLanguageCode(), slug, localesMgr.isCookiesProcessed()]);

	useHandlePageEvents(
		{
			pageType: isHomePage ? EEventSource.HOME : EEventSource.CLP,
		},
		[currentLocale.getCode(), slug],
	);

	return null;
};

const CLPContent = (props: Props) => {
	const {
		content,
		localizedContent,
		isHomePage,
		slug,
		routeInfo
	} = props;
	const location = useLocation();
	const setUI = useSetUI();

	const {metadata, pageUrls, canonical} = useMemo(() => {
		const localMetadata = [];
		const pageUrls = {...routeInfo.pageUrls};

		Object.keys(pageUrls).map((lang) => {
			if (content?.slug[lang]) {
				pageUrls[lang] = content?.slug[lang];
			}
		});

		if (localizedContent?.seo) {
			const pageMeta = buildPageMetadata(localizedContent.seo);

			if (pageMeta) {
				localMetadata.push(pageMeta);
			}
		}

		if (routeInfo?.metadata) {
			localMetadata.push(routeInfo.metadata);
		}

		return {
			metadata: localMetadata,
			pageUrls,
			canonical: absoluteUrl(location.pathname),
		};
	}, [routeInfo, content, localizedContent, location.pathname]);

	usePageInit({
		route: `${slug} page`,
		isHomePage: isHomePage,
		cache: MAX_CACHE_AGE,
		seo: {
			metadata,
			pageUrls,
			jsonld: routeInfo?.jsonld,
			canonical,
		}
	});

	useClientServerEffect(() => {
		if (localizedContent?.baseNavigationColor) {
			setUI.setHeader({type: EHeaderType.WHITE});
		}

		return () => {
			setUI.setHeader({type: EHeaderType.DEFAULT});
		};
	}, [localizedContent]);

	return (
		<>
			<CLPEffects {...props} />
			<Contentful initialData={localizedContent} />
		</>
	);
};

export default withSSRPage(CLPContent, 'clp');
